import * as Sentry from '@sentry/react'
import { useState, type FC } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Spinner } from '../../../components/Spinner/Spinner'
import { BookingStatus, type SchedulingFilters, type Booking } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  useCancelBookingMutation,
  useGetBookingsQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
} from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { groupBookingsByMonth, startOfCurrentMinute, toBookingsFilterArgs } from '../bookingTabs'
import { BookingActionMenu } from '../rows/BookingActionMenu'
import { BookingRow } from '../rows/BookingRow'
import { BookingReasonDialog } from '../shared/BookingReasonDialog'
import { getMeetingPrepIds, meetingPrepPath, reschedulePath } from '../shared/bookingNavigation'
import { SchedulingEmptyState } from './SchedulingEmptyState'
import { SchedulingTabErrorCard } from './SchedulingTabErrorCard'

export const ScheduledTab: FC<{ filters: SchedulingFilters }> = ({ filters }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const {
    data: bookingsResponse,
    isFetching,
    isError,
    refetch,
  } = useGetBookingsQuery({
    ...toBookingsFilterArgs(filters),
    accountId: authenticatedUser.accountId,
    status: BookingStatus.CONFIRMED,
    dateFrom: startOfCurrentMinute(),
    pageRequest: { page: page - 1, size: pageSize },
  })
  const bookings = bookingsResponse?.data
  const totalElements = bookingsResponse?.page.totalElements ?? 0
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId: authenticatedUser.accountId })
  const [cancelBooking, { isLoading: isCancelling }] = useCancelBookingMutation()
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null)

  if (isFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <SchedulingTabErrorCard title="Couldn't load scheduled meetings" onRetry={refetch} />
  }

  if (!Array.isArray(bookings) || bookings.length === 0) {
    return <SchedulingEmptyState title='No scheduled meetings' subtitle='Meetings appear here once a time is set.' />
  }

  const eventTypeById = new Map((eventTypes ?? []).map((eventType) => [eventType.id, eventType]))
  const locationById = new Map((locations ?? []).map((location) => [location.id, location]))
  const monthGroups = groupBookingsByMonth(bookings)

  const handleCopyJoinLink = async (booking: Booking) => {
    if (!booking.meetingUrl) {
      dispatch(addToast({ variant: 'info', title: 'No join link yet', messages: ['A join link appears once the meeting is ready.'] }))
      return
    }
    try {
      await navigator.clipboard.writeText(booking.meetingUrl)
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy the link', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_JOIN_LINK_COPIED, eventDetails: { bookingId: booking.bookingId } })
    dispatch(addToast({ variant: 'success', title: 'Join link copied', messages: ['Paste it anywhere to share.'] }))
  }

  const handleCancel = async (reason: string) => {
    if (!cancelTarget) {
      return
    }
    const bookingId = cancelTarget.bookingId
    const result = await cancelBooking({ accountId: authenticatedUser.accountId, payload: { bookingId, reason: reason || undefined } })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not cancel the meeting', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_MEETING_CANCELLED, eventDetails: { bookingId } })
    dispatch(addToast({ variant: 'success', title: 'Meeting canceled', messages: [`${cancelTarget.title ?? 'The meeting'} was canceled.`] }))
    setCancelTarget(null)
  }

  return (
    <>
      <div className='d-flex flex-column gap-3'>
        {monthGroups.map((group) => (
          <div key={group.monthKey} className='d-flex flex-column gap-2'>
            <div className='lead-small zocks-text-gray-500'>{group.monthLabel}</div>
            {group.bookings.map((booking) => {
              const prepIds = getMeetingPrepIds(booking)
              return (
                <BookingRow
                  key={booking.bookingId}
                  booking={booking}
                  eventType={booking.eventTypeId ? eventTypeById.get(booking.eventTypeId) : undefined}
                  location={booking.location?.id ? locationById.get(booking.location.id) : undefined}
                >
                  {prepIds ? (
                    <Button variant='primaryGhost' onClick={() => navigate(meetingPrepPath(prepIds.roomId, prepIds.sessionId))}>
                      Meeting prep
                    </Button>
                  ) : null}
                  <Button variant='grayButton' onClick={() => navigate(reschedulePath(booking.bookingId))}>
                    Reschedule
                  </Button>
                  <BookingActionMenu ariaLabel={`${booking.title ?? 'Meeting'} actions`}>
                    <Dropdown.Item onClick={() => handleCopyJoinLink(booking)}>
                      <i className='bi bi-link-45deg me-1' /> Copy join link
                    </Dropdown.Item>
                    <Dropdown.Item className='text-danger' onClick={() => setCancelTarget(booking)}>
                      <i className='bi bi-slash-circle me-1' /> Cancel meeting
                    </Dropdown.Item>
                  </BookingActionMenu>
                </BookingRow>
              )
            })}
          </div>
        ))}
      </div>
      <CustomPaginationComponent
        rowsPerPage={pageSize}
        rowCount={totalElements}
        currentPage={page}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize) => {
          setPage(1)
          setPageSize(newPageSize)
        }}
      />
      {cancelTarget ? (
        <BookingReasonDialog
          title='Canceling meeting?'
          body="We'll notify everyone invited that this meeting was canceled."
          confirmLabel='Cancel meeting'
          cancelLabel='Keep meeting'
          isLoading={isCancelling}
          onConfirm={handleCancel}
          onCancel={() => setCancelTarget(null)}
        />
      ) : null}
    </>
  )
}
